import { useEffect, useRef, useState } from "react";

/**
 * Reveal — wraps children and triggers a soft, premium entrance animation
 * when the element scrolls into view.
 *
 * Props:
 *  - variant: "fade-up" | "fade" | "fade-left" | "fade-right" | "zoom-soft"
 *  - delay: ms
 *  - duration: ms
 *  - once: only trigger once (default true)
 */
export default function Reveal({
  children,
  variant = "fade-up",
  delay = 0,
  duration = 900,
  once = true,
  className = "",
  as: Tag = "div",
  style = {},
  ...rest
}) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
            if (once) io.unobserve(entry.target);
          } else if (!once) {
            setInView(false);
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -60px 0px" }
    );

    io.observe(el);
    return () => io.disconnect();
  }, [once]);

  const base = "cm-reveal";
  const cls = `${base} ${base}-${variant} ${inView ? "is-in" : ""} ${className}`.trim();

  return (
    <Tag
      ref={ref}
      className={cls}
      style={{
        ...style,
        transitionDuration: `${duration}ms`,
        transitionDelay: `${delay}ms`,
      }}
      {...rest}
    >
      {children}
    </Tag>
  );
}
